import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { ShoppingBasket, Plus, Trash2, ArrowLeft, CheckCircle2, XCircle, CreditCard, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  in_attesa: "In attesa di un rider", rider_assegnato: "Rider assegnato",
  in_acquisto: "In acquisto al mercato", acquistato: "Acquistato",
  in_consegna: "In consegna", consegnato: "Consegnato", annullato: "Annullato",
};

export default function MarketErrands() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([{ name: "", qty: 1, note: "" }]);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [list, setList] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/my/errands"); setList(data); } catch { /* */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    const s = params.get("paid_session");
    if (!s) return;
    api.get(`/payments/status/${s}`).then(({ data }) => {
      if (data.payment_status === "paid") toast.success("Pagamento completato!");
      load(); setParams({});
    }).catch(() => setParams({}));
  }, [params, setParams, load]);

  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems([...items, { name: "", qty: 1, note: "" }]);
  const delRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const create = async () => {
    const clean = items.filter((i) => i.name.trim());
    if (!clean.length) { toast.error("Aggiungi almeno un prodotto"); return; }
    if (!address.trim()) { toast.error("Indirizzo di consegna richiesto"); return; }
    try {
      await api.post("/errands", { service_type: "market", items: clean.map((i) => ({ name: i.name, qty: parseInt(i.qty) || 1, note: i.note })), address, phone, notes });
      setItems([{ name: "", qty: 1, note: "" }]); setNotes("");
      toast.success("Lista inviata! Un Rider Mercato la prenderà in carico.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pay = async (id) => {
    try {
      const { data } = await api.post(`/errands/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Annullare la commissione?")) return;
    try { await api.put(`/errands/${id}/cancel`); load(); toast.success("Annullata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="mercato-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/esplora")} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 text-sm" data-testid="mercato-back"><ArrowLeft className="h-4 w-4" /> Torna alla mappa</button>
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center"><ShoppingBasket className="h-6 w-6 text-[#00E676]" /></div>
          <div><h1 className="text-2xl font-display font-bold">Rider Mercato</h1><p className="text-sm text-zinc-400">Crea la lista, un rider fa la spesa e te la consegna.</p></div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5 mb-8 space-y-4">
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input data-testid={`errand-item-name-${i}`} placeholder="Prodotto (es. Pomodori)" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} className="bg-[#0A0A0C] border-white/10 flex-1" />
                <Input data-testid={`errand-item-qty-${i}`} type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="bg-[#0A0A0C] border-white/10 w-20" />
                <button onClick={() => delRow(i)} className="p-2 text-red-400 hover:bg-white/5 rounded" data-testid={`errand-del-item-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="secondary" size="sm" onClick={addRow} data-testid="errand-add-item"><Plus className="h-4 w-4 mr-1" /> Aggiungi prodotto</Button>
          </div>
          <div><Label>Indirizzo di consegna</Label><Input data-testid="errand-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero, comune" className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Telefono</Label><Input data-testid="errand-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          </div>
          <div><Label>Note (banchi preferiti, preferenze…)</Label><Textarea data-testid="errand-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <p className="text-xs text-zinc-500">Pagherai il costo reale della spesa + commissione di servizio (12%) + consegna. Il totale viene calcolato dopo l'acquisto del rider.</p>
          <Button onClick={create} data-testid="create-errand-btn" className="bg-[#00E676] text-black rounded-md w-full">Invia lista al Rider Mercato</Button>
        </div>

        <h2 className="text-lg font-semibold mb-3">Le mie commissioni</h2>
        <div className="space-y-3">
          {list.map((e) => (
            <div key={e.id} data-testid={`my-errand-${e.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">#{e.id.slice(-5)}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 flex items-center gap-1"><Clock className="h-3 w-3" /> {STATUS_LABEL[e.status] || e.status}</span>
              </div>
              <div className="text-sm text-zinc-400 space-y-1 mb-2">
                {e.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {it.purchased ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00E676]" /> : it.unavailable ? <XCircle className="h-3.5 w-3.5 text-red-400" /> : <span className="h-3.5 w-3.5 inline-block rounded-full border border-zinc-600" />}
                    <span>{it.qty}× {it.name}</span>
                    {it.actual_price != null && <span className="text-[#00E676]">€{Number(it.actual_price).toFixed(2)}</span>}
                    {it.unavailable && <span className="text-red-400 text-xs">non disponibile</span>}
                  </div>
                ))}
              </div>
              {e.total > 0 && (
                <div className="text-sm border-t border-white/5 pt-2 mb-2">
                  <div className="flex justify-between text-zinc-400"><span>Spesa</span><span>€{Number(e.items_actual_total).toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Commissione</span><span>€{Number(e.service_commission).toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Consegna</span><span>€{Number(e.delivery_fee).toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold text-white mt-1"><span>Totale</span><span className="text-[#00E676]">€{Number(e.total).toFixed(2)}</span></div>
                </div>
              )}
              <div className="flex gap-2">
                {["in_attesa", "rider_assegnato"].includes(e.status) && <Button size="sm" variant="destructive" onClick={() => cancel(e.id)} data-testid={`cancel-errand-${e.id}`}>Annulla</Button>}
                {e.total > 0 && e.payment_status !== "paid" && ["acquistato", "in_consegna", "consegnato"].includes(e.status) && (
                  <Button size="sm" onClick={() => pay(e.id)} data-testid={`pay-errand-${e.id}`} className="bg-[#00E676] text-black rounded-md"><CreditCard className="h-4 w-4 mr-1" /> Paga €{Number(e.total).toFixed(2)}</Button>
                )}
                {e.payment_status === "paid" && <span className="text-sm text-[#00E676] flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Pagato</span>}
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="text-zinc-500 py-10 text-center">Nessuna commissione ancora.</div>}
        </div>
      </div>
    </div>
  );
}
